import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiFetch, type Me, type SettingGroupSummary } from './api.ts';
import { useSession } from './session.tsx';
import { searchSettings, settingsNavSections, settingsSearchIndex, visibleSettingsPages, type SettingsNavSection } from '../settings-pages.ts';

/** Nur die Einstellungen samt Unterseiten bilden den Kontext für lokale Treffer. */
export function isSettingsSearchContext(pathname: string): boolean {
  return pathname === '/einstellungen' || pathname.startsWith('/einstellungen/');
}

/** Eine gemeinsame Abfrage für Navigation und Suche; keine Abfrage außerhalb der Einstellungen. */
export function useSettingGroups(enabled = true) {
  const { getToken, me } = useSession();
  return useQuery({
    queryKey: ['settings', 'groups', 'mcp', me.id, me.role],
    queryFn: () => apiFetch<{ groups: SettingGroupSummary[] }>(getToken, '/api/v1/settings?include=mcp'),
    enabled,
    // Der Suchdialog nutzt die bereits geladene Navigation, ohne beim Öffnen erneut abzufragen.
    refetchOnMount: false,
  });
}

/** Ohne geladene Gruppen entsteht kein vermeintlich vollständiger Einstellungsindex. */
export function settingsSearchSections(groups: SettingGroupSummary[] | undefined, me: Me): SettingsNavSection[] | null {
  return groups ? settingsNavSections(groups, visibleSettingsPages(me)) : null;
}

export function useSettingsSections(): SettingsNavSection[] | null {
  const { me } = useSession();
  const groups = useSettingGroups();
  return useMemo(() => settingsSearchSections(groups.data?.groups, me), [groups.data, me]);
}

export function localSettingsHits(pathname: string, sections: SettingsNavSection[] | null, query: string) {
  return isSettingsSearchContext(pathname) && sections ? searchSettings(settingsSearchIndex(sections), query) : [];
}

/** Gleiche Seite: übrige Parameter bleiben erhalten, der bestehende Verlassen-Schutz bleibt zuständig. */
export function settingsJumpHref(href: string, pathname: string, searchStr: string): string {
  const target = new URL(href, 'http://x');
  if (target.pathname !== pathname) return href;
  const merged = new URLSearchParams(searchStr);
  merged.delete('feld');
  merged.delete('karte');
  for (const [key, value] of target.searchParams) merged.set(key, value);
  const query = merged.toString();
  return query ? `${pathname}?${query}` : pathname;
}
